import { Orientation, DeviceType, WriteDirection, Platform } from 'sofa-reborn-react';
import styled from 'styled-components';
import { SkapaTheme } from '@inter-ikea-kompis/themes';

type Attributes = {
	orientation: Orientation;
};

type ExtendedAttributes = {
	orientation: Orientation;
	writeDirection: WriteDirection;
	dragSceneItemId: string;
	overSceneItemId: string;
	pointerOverDragArea: boolean;
};

function getBorderStyle(props: Attributes): string {
	return props.orientation === 'portrait'
		? `
				border-top: 1px solid ${SkapaTheme.color.border.light};
		  `
		: `
				border-left: 1px solid ${SkapaTheme.color.border.light};
		  `;
}

function getSelectorHeight(deviceType: DeviceType, orientation: Orientation): string {
	if (deviceType === 'tablet') {
		return `
			height: 150px;
		`;
	} else if (deviceType === 'mobile' && orientation === 'portrait') {
		return `
			height: 110px;
		`;
	} else {
		return ``;
	}
}

function getOrder(props: ExtendedAttributes): string {
	return props.orientation !== 'portrait' && props.writeDirection === 'rtl'
		? `
				order: 2;
		  `
		: `
				order: 0;
		  `;
}

function getCursor(props: ExtendedAttributes): string {
	if (props.pointerOverDragArea) {
		if (props.dragSceneItemId) {
			return `
				cursor: grabbing;
			`;
		} else {
			return props.overSceneItemId
				? `
						cursor: grab;
				  `
				: ``;
		}
	} else {
		return ``;
	}
}

function getDivider(
	orientation: Orientation,
	writeDirection: WriteDirection,
	deviceType: DeviceType,
	hasArmrestPicker: boolean
): string {
	return `
		border-right: ${
			orientation === 'portrait' && hasArmrestPicker && writeDirection === 'ltr'
				? `1px solid ${SkapaTheme.color.border.light}`
				: 'none'
		};
		border-left: ${
			orientation === 'portrait' && hasArmrestPicker && writeDirection === 'rtl'
				? `1px solid ${SkapaTheme.color.border.light}`
				: 'none'
		};
		border-bottom: ${
			deviceType === 'mobile' && orientation === 'landscape'
				? `1px solid ${SkapaTheme.color.border.light}`
				: 'none'
		};
	`;
}

export const DivBuildArea = styled.div<{
	deviceType: DeviceType;
	orientation: Orientation;
	platform: Platform;
	writeDirection: WriteDirection;
}>`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	flex-basis: auto;
	width: ${(props) =>
		props.deviceType === 'mobile' && props.orientation === 'landscape'
			? 'calc(100% - 115px)'
			: props.deviceType === 'desktop'
			? props.platform === 'kiosk'
				? 'calc(100% - 435px)'
				: 'calc(100% - 337px)'
			: '100%'};
	overflow: hidden;
	align-self: ${(props) =>
		props.orientation === 'landscape' && props.writeDirection === 'rtl' ? 'flex-end' : 'auto'};
`;

export const DivSelector = styled.div<{
	deviceType: DeviceType;
	orientation: Orientation;
	platform: Platform;
	writeDirection: WriteDirection;
}>`
	display: flex;
	position: ${(props) => (props.orientation === 'landscape' ? 'absolute' : '')};
	right: 0;
	flex-direction: ${(props) => (props.orientation === 'portrait' ? 'row' : 'column')};
	height: calc(100% - 2px);
	width: ${(props) =>
		props.deviceType === 'mobile' && props.orientation === 'landscape' ? '115px' : 'auto'};
	order: ${(props) =>
		props.orientation !== 'portrait' && props.writeDirection === 'rtl' ? '-1' : '0'};
	${(props) => getSelectorHeight(props.deviceType, props.orientation)};
	${(props) => getBorderStyle(props)};
`;

export const BuildAreaWrapper = styled.div<{
	writeDirection: WriteDirection;
	orientation: Orientation;
	platform: Platform;
	deviceType: DeviceType;
	dragSceneItemId: string;
	overSceneItemId: string;
	pointerOverDragArea: boolean;
	footerHeight: number;
}>`
	height: ${(props) =>
		props.orientation === 'portrait' ? 'calc(100% - ' + props.footerHeight + 'px)' : '100%'};
	width: 100%;
	display: flex;
	${(props) => getOrder(props)};
	border-bottom: ${(props) =>
		props.deviceType !== 'desktop' ? '1px solid ' + SkapaTheme.color.border.default : ''};
	flex-direction: column;
	${(props) => getCursor(props)};
	${(props) => props.deviceType === 'tablet' && 'padding-bottom: 1px;'}
`;

export const PickersStyle = styled.div<{
	orientation: Orientation;
	deviceType: DeviceType;
	writeDirection: WriteDirection;
	hasArmrestPicker: boolean;
}>`
	padding: ${(props) =>
		props.deviceType === 'desktop'
			? `${SkapaTheme.spacing.space25}px ${SkapaTheme.spacing.space25}px ${SkapaTheme.spacing.space25}px 0`
			: '0'};
	display: flex;
	flex-direction: ${(props) =>
		props.orientation === 'landscape' && props.deviceType === 'mobile' ? 'row' : 'column'};
	${(props) =>
		getDivider(props.orientation, props.writeDirection, props.deviceType, props.hasArmrestPicker)};
	${(props) =>
		props.deviceType === 'mobile' &&
		props.orientation === 'landscape' &&
		'justify-content: center'};
	${(props) => props.deviceType === 'tablet' && 'justify-content: space-evenly'};

	#coverPickerButton.focus-visible,
	#armrestPickerButton.focus-visible {
		border-radius: 2px;
		box-shadow: 0 0 0 ${SkapaTheme.focusOutline.width}px ${SkapaTheme.focusOutline.color};
		z-index: 10;
	}
`;

export const CoverOnlyDisclaimerStyle = styled.div<{
	platform: Platform;
	right: string;
}>`
	position: absolute;
	bottom: ${(props) => (props.platform === 'kiosk' ? '125px' : '90px')};
	right: ${(props) => props.right};
`;
